import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { media } from '../../styles/theme';

// Largura máxima do conteúdo e altura do header fixo
const LARGURA = '1120px';
const ALTURA_HEADER = '64px';

export const Pagina = styled.div`
  min-height: 100dvh;
  background-color: ${({ theme }) => theme.cores.card};
  color: ${({ theme }) => theme.cores.ink};

  ${media.mobile} {
    padding-bottom: 76px; // barra de ação fixa
  }
`;

export const Conteudo = styled.div`
  width: 100%;
  max-width: ${LARGURA};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.espaco.lg};
`;

// ---------- Botões-link (âncora externa ou rota interna) ----------

type VarianteLink = 'primario' | 'accent' | 'whatsapp' | 'contorno' | 'contornoInverso';

const variantesLink = {
  primario: css`
    background-color: ${({ theme }) => theme.cores.primary};
    color: ${({ theme }) => theme.cores.inkInverse};
    &:hover { background-color: ${({ theme }) => theme.cores.primaryHover}; }
  `,
  accent: css`
    background-color: ${({ theme }) => theme.cores.accent};
    color: ${({ theme }) => theme.cores.primary};
    &:hover { filter: brightness(0.95); }
  `,
  whatsapp: css`
    background-color: ${({ theme }) => theme.cores.whatsapp};
    color: ${({ theme }) => theme.cores.inkInverse};
    &:hover { background-color: ${({ theme }) => theme.cores.whatsappHover}; }
  `,
  contorno: css`
    background-color: ${({ theme }) => theme.cores.card};
    border-color: ${({ theme }) => theme.cores.border};
    color: ${({ theme }) => theme.cores.primary};
    &:hover { border-color: ${({ theme }) => theme.cores.borderStrong}; background-color: ${({ theme }) => theme.cores.surface}; }
  `,
  contornoInverso: css`
    background-color: transparent;
    border-color: ${({ theme }) => theme.cores.borderInverse};
    color: ${({ theme }) => theme.cores.inkInverse};
    &:hover { background-color: ${({ theme }) => theme.cores.primarySoft}; }
  `,
};

const estiloBotaoLink = css<{ $variante: VarianteLink; $pequeno?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.espaco.sm};
  height: ${({ $pequeno }) => ($pequeno ? '38px' : '48px')};
  padding: 0 ${({ theme, $pequeno }) => ($pequeno ? theme.espaco.lg : theme.espaco.xl)};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.raio.md};
  font-size: ${({ theme, $pequeno }) => ($pequeno ? theme.fonte.small : theme.fonte.input)};
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, filter 0.15s ease;

  svg { flex-shrink: 0; font-size: 1.15em; }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.cores.accent};
    outline-offset: 2px;
  }

  ${({ $variante }) => variantesLink[$variante]}

  ${media.mobile} {
    height: ${({ $pequeno }) => ($pequeno ? '40px' : '48px')};
  }
`;

export const BotaoRota = styled(Link)<{ $variante: VarianteLink; $pequeno?: boolean }>`
  ${estiloBotaoLink}
`;

export const BotaoExterno = styled.a<{ $variante: VarianteLink; $pequeno?: boolean }>`
  ${estiloBotaoLink}
`;

export const GrupoBotoes = styled.div<{ $centro?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.espaco.md};
  justify-content: ${({ $centro }) => ($centro ? 'center' : 'flex-start')};

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

// ---------- Header ----------

export const Header = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: ${({ theme }) => theme.zIndex.header};
  height: ${ALTURA_HEADER};
  background-color: ${({ theme }) => theme.cores.primary};
  border-bottom: 1px solid ${({ theme }) => theme.cores.borderInverse};

  ${Conteudo} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.espaco.md};
    height: 100%;
  }
`;

export const Marca = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.sm};
  min-width: 0;
  color: ${({ theme }) => theme.cores.inkInverse};
  text-decoration: none;

  img { height: 32px; width: auto; }

  strong { font-size: ${({ theme }) => theme.fonte.h3}; white-space: nowrap; }

  span {
    color: ${({ theme }) => theme.cores.inkInverseMuted};
    font-size: ${({ theme }) => theme.fonte.small};
    white-space: nowrap;
  }

  ${media.mobile} {
    span { display: none; }
  }
`;

export const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.xs};

  a.ancora {
    padding: ${({ theme }) => theme.espaco.sm} ${({ theme }) => theme.espaco.md};
    border-radius: ${({ theme }) => theme.raio.sm};
    color: ${({ theme }) => theme.cores.inkInverseMuted};
    font-size: ${({ theme }) => theme.fonte.body};
    text-decoration: none;

    &:hover { color: ${({ theme }) => theme.cores.inkInverse}; }
  }

  ${BotaoRota} { margin-left: ${({ theme }) => theme.espaco.sm}; }

  ${media.mobile} {
    a.ancora { display: none; }
  }
`;

// ---------- Seções ----------

export const Secao = styled.section<{ $fundo?: 'surface' | 'card' }>`
  scroll-margin-top: ${ALTURA_HEADER};
  padding: 88px 0;
  background-color: ${({ theme, $fundo }) => ($fundo === 'surface' ? theme.cores.surface : theme.cores.card)};

  ${media.mobile} {
    padding: 56px 0;
  }
`;

export const Rotulo = styled.p`
  color: ${({ theme }) => theme.cores.inkSecondary};
  font-size: ${({ theme }) => theme.fonte.caption};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const TituloSecao = styled.h2`
  max-width: 680px;
  margin-top: ${({ theme }) => theme.espaco.sm};
  font-size: clamp(26px, 3.2vw, 36px);
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

export const TextoSecao = styled.p`
  max-width: 640px;
  margin-top: ${({ theme }) => theme.espaco.md};
  color: ${({ theme }) => theme.cores.inkSecondary};
  font-size: ${({ theme }) => theme.fonte.h3};
  line-height: 1.6;
`;

// ---------- Hero ----------

export const Hero = styled.section`
  padding: calc(${ALTURA_HEADER} + 72px) 0 88px;
  background-color: ${({ theme }) => theme.cores.primary};
  color: ${({ theme }) => theme.cores.inkInverse};

  ${Conteudo} {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    align-items: center;
    gap: 56px;
  }

  h1 {
    margin-top: ${({ theme }) => theme.espaco.lg};
    font-size: clamp(32px, 4.6vw, 50px);
    line-height: 1.1;
    letter-spacing: -0.02em;

    em {
      font-style: normal;
      color: ${({ theme }) => theme.cores.accent};
    }
  }

  .descricao {
    max-width: 560px;
    margin: ${({ theme }) => theme.espaco.lg} 0 ${({ theme }) => theme.espaco.xxl};
    color: ${({ theme }) => theme.cores.inkInverseMuted};
    font-size: 18px;
    line-height: 1.6;
  }

  ${media.mobile} {
    padding: calc(${ALTURA_HEADER} + 40px) 0 56px;

    ${Conteudo} {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .descricao { font-size: ${({ theme }) => theme.fonte.h3}; }
  }
`;

export const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.sm};
  padding: 6px ${({ theme }) => theme.espaco.md};
  border-radius: ${({ theme }) => theme.raio.pill};
  background-color: ${({ theme }) => theme.cores.heroDestaque};
  color: ${({ theme }) => theme.cores.accent};
  font-size: ${({ theme }) => theme.fonte.small};
  font-weight: 600;
`;

export const ListaChecks = styled.ul<{ $inverso?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.espaco.sm} ${({ theme }) => theme.espaco.xl};
  margin-top: ${({ theme }) => theme.espaco.xl};
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.espaco.sm};
    color: ${({ theme, $inverso }) => ($inverso ? theme.cores.inkInverseMuted : theme.cores.inkSecondary)};
    font-size: ${({ theme }) => theme.fonte.body};

    svg { color: ${({ theme, $inverso }) => ($inverso ? theme.cores.accent : theme.cores.good)}; flex-shrink: 0; }
  }
`;

// Prévia ilustrativa de uma tela do sistema (sem dados reais)
export const Previa = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.cores.borderInverse};
  border-radius: ${({ theme }) => theme.raio.card};
  background-color: ${({ theme }) => theme.cores.surface};
  box-shadow: ${({ theme }) => theme.sombra.modal};
  color: ${({ theme }) => theme.cores.ink};

  .barra {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.espaco.sm};
    height: 40px;
    padding: 0 ${({ theme }) => theme.espaco.md};
    background-color: ${({ theme }) => theme.cores.primaryHover};
    color: ${({ theme }) => theme.cores.inkInverse};
    font-size: ${({ theme }) => theme.fonte.small};
    font-weight: 600;

    i {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.cores.accent};
    }
  }

  .corpo {
    display: grid;
    gap: ${({ theme }) => theme.espaco.md};
    padding: ${({ theme }) => theme.espaco.lg};
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${({ theme }) => theme.espaco.sm};
  }

  .kpi, .grafico {
    padding: ${({ theme }) => theme.espaco.md};
    border: 1px solid ${({ theme }) => theme.cores.border};
    border-radius: ${({ theme }) => theme.raio.md};
    background-color: ${({ theme }) => theme.cores.card};
  }

  .kpi small, .grafico small {
    display: block;
    color: ${({ theme }) => theme.cores.inkMuted};
    font-size: ${({ theme }) => theme.fonte.caption};
  }

  .kpi strong {
    display: block;
    margin-top: 2px;
    font-size: ${({ theme }) => theme.fonte.h3};
  }

  .barras {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    height: 96px;
    margin-top: ${({ theme }) => theme.espaco.md};
    border-bottom: 1px solid ${({ theme }) => theme.cores.grade};

    span {
      flex: 1;
      border-radius: 4px 4px 0 0;
      background-color: ${({ theme }) => theme.cores.serie[0]};
    }
  }

  ${media.mobile} {
    .kpi strong { font-size: ${({ theme }) => theme.fonte.body}; }
  }
`;

// ---------- Cards ----------

export const GradeCards = styled.div<{ $colunas?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $colunas = 3 }) => $colunas}, 1fr);
  gap: ${({ theme }) => theme.espaco.lg};
  margin-top: 48px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
    margin-top: 32px;
  }
`;

export const CardSolucao = styled.article`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.espaco.xl};
  border: 1px solid ${({ theme }) => theme.cores.border};
  border-radius: ${({ theme }) => theme.raio.card};
  background-color: ${({ theme }) => theme.cores.card};
  box-shadow: ${({ theme }) => theme.sombra.card};

  h3 {
    margin-top: ${({ theme }) => theme.espaco.lg};
    font-size: 18px;
  }

  p {
    margin-top: ${({ theme }) => theme.espaco.sm};
    color: ${({ theme }) => theme.cores.inkSecondary};
    font-size: ${({ theme }) => theme.fonte.body};
    line-height: 1.6;
  }

  ul {
    display: grid;
    gap: ${({ theme }) => theme.espaco.sm};
    margin-top: ${({ theme }) => theme.espaco.lg};
    padding-top: ${({ theme }) => theme.espaco.lg};
    border-top: 1px solid ${({ theme }) => theme.cores.border};
    list-style: none;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.espaco.sm};
    color: ${({ theme }) => theme.cores.ink};
    font-size: ${({ theme }) => theme.fonte.body};

    svg { margin-top: 3px; flex-shrink: 0; color: ${({ theme }) => theme.cores.good}; }
  }
`;

export const Icone = styled.span<{ $suave?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.raio.md};
  background-color: ${({ theme, $suave }) => ($suave ? theme.cores.selecionado : theme.cores.primary)};
  color: ${({ theme, $suave }) => ($suave ? theme.cores.primary : theme.cores.accent)};
  font-size: 20px;
`;

export const GradeDetalhes = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  margin-top: 48px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.cores.border};
  border-radius: ${({ theme }) => theme.raio.card};
  background-color: ${({ theme }) => theme.cores.border};

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
    margin-top: 32px;
  }
`;

export const Detalhe = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.espaco.lg};
  padding: ${({ theme }) => theme.espaco.xl};
  background-color: ${({ theme }) => theme.cores.card};

  h3 { font-size: ${({ theme }) => theme.fonte.h3}; }

  p {
    margin-top: ${({ theme }) => theme.espaco.xs};
    color: ${({ theme }) => theme.cores.inkSecondary};
    font-size: ${({ theme }) => theme.fonte.body};
    line-height: 1.5;
  }
`;

// ---------- Empresa ----------

export const Empresa = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  align-items: center;
  gap: 56px;

  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

export const Numeros = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.espaco.lg};

  div {
    padding: ${({ theme }) => theme.espaco.xl};
    border: 1px solid ${({ theme }) => theme.cores.border};
    border-radius: ${({ theme }) => theme.raio.card};
    background-color: ${({ theme }) => theme.cores.card};
  }

  dt {
    color: ${({ theme }) => theme.cores.inkSecondary};
    font-size: ${({ theme }) => theme.fonte.small};
  }

  dd {
    margin-top: ${({ theme }) => theme.espaco.xs};
    color: ${({ theme }) => theme.cores.primary};
    font-size: ${({ theme }) => theme.fonte.h2};
    font-weight: 700;
  }
`;

// ---------- Contato ----------

export const CardContato = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.lg};
  padding: ${({ theme }) => theme.espaco.xl};
  border: 1px solid ${({ theme }) => theme.cores.border};
  border-radius: ${({ theme }) => theme.raio.card};
  background-color: ${({ theme }) => theme.cores.card};
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &[href]:hover { border-color: ${({ theme }) => theme.cores.borderStrong}; }

  small {
    display: block;
    color: ${({ theme }) => theme.cores.inkSecondary};
    font-size: ${({ theme }) => theme.fonte.small};
  }

  strong {
    display: block;
    margin-top: 2px;
    font-size: ${({ theme }) => theme.fonte.h3};
    overflow-wrap: anywhere;
  }
`;

export const Chamada = styled.div`
  margin-top: 56px;
  padding: 56px ${({ theme }) => theme.espaco.xxl};
  border-radius: 20px;
  background-color: ${({ theme }) => theme.cores.primary};
  color: ${({ theme }) => theme.cores.inkInverse};
  text-align: center;

  h2 {
    max-width: 640px;
    margin: 0 auto;
    font-size: clamp(24px, 3vw, 34px);
    line-height: 1.2;
  }

  p {
    max-width: 520px;
    margin: ${({ theme }) => theme.espaco.md} auto ${({ theme }) => theme.espaco.xxl};
    color: ${({ theme }) => theme.cores.inkInverseMuted};
    font-size: ${({ theme }) => theme.fonte.h3};
  }

  ${media.mobile} {
    padding: 40px ${({ theme }) => theme.espaco.lg};
  }
`;

// ---------- Rodapé e ações flutuantes ----------

export const Rodape = styled.footer`
  border-top: 1px solid ${({ theme }) => theme.cores.border};

  ${Conteudo} {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.espaco.md};
    padding-top: ${({ theme }) => theme.espaco.xl};
    padding-bottom: ${({ theme }) => theme.espaco.xl};
    color: ${({ theme }) => theme.cores.inkSecondary};
    font-size: ${({ theme }) => theme.fonte.small};
  }

  a {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.espaco.sm};
    color: inherit;
    text-decoration: none;

    &:hover { color: ${({ theme }) => theme.cores.ink}; }

    img { width: 22px; height: 22px; }
  }
`;

export const WhatsappFlutuante = styled.a`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.cores.whatsapp};
  color: ${({ theme }) => theme.cores.inkInverse};
  font-size: 28px;
  box-shadow: ${({ theme }) => theme.sombra.dropdown};
  transition: transform 0.15s ease, background-color 0.15s ease;

  &:hover {
    transform: scale(1.06);
    background-color: ${({ theme }) => theme.cores.whatsappHover};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover { transform: none; }
  }

  ${media.mobile} {
    display: none;
  }
`;

export const BarraMobile = styled.div`
  display: none;

  ${media.mobile} {
    position: fixed;
    inset: auto 0 0 0;
    z-index: ${({ theme }) => theme.zIndex.dropdown};
    display: flex;
    gap: ${({ theme }) => theme.espaco.sm};
    padding: ${({ theme }) => theme.espaco.md} ${({ theme }) => theme.espaco.lg} calc(${({ theme }) => theme.espaco.md} + env(safe-area-inset-bottom));
    border-top: 1px solid ${({ theme }) => theme.cores.border};
    background-color: ${({ theme }) => theme.cores.card};

    ${BotaoExterno} {
      width: 48px;
      padding: 0;
      flex-shrink: 0;
    }

    ${BotaoRota} { flex: 1; }
  }
`;
